import { cn } from "@/utils/cn.ts";
import FormGlobalError from "@babackapp/react-hook-forms-components/form-global-error";
import FormSaveButton from "@babackapp/react-hook-forms-components/form-save-button";
import { Button } from "@babackapp/ui/components/button";
import { Form } from "@babackapp/ui/components/form";
import { Tabs, TabsContent } from "@babackapp/ui/components/tabs";
import { useForm } from "react-hook-form";
import { ReturnReasonFormValues } from "./add-return-reason-dialog.tsx";
import ReturnReasonFormTabsList from "./return-reason-form-tabs-list.tsx";
import ReturnReasonReturnRulesForm from "./return-reason-rules-form.tsx";
import ReturnReasonTranslationsForm from "./return-reason-translations-form.tsx";
import ReturnReasonOptionsForm from "./return-reasons-options-form.tsx";
import useTranslation from "@babackapp/translations/use-translation";

type ReturnReasonFormProps = {
  onSubmit: (values: ReturnReasonFormValues) => void;
  onRemove?: () => void;
  defaultValues?: Partial<ReturnReasonFormValues>;
  setError?: (error: { title?: string; error?: string } | null) => void;
  error?: {
    title?: string;
    error?: string;
  } | null;
};

const DEFAULT_VALUES: ReturnReasonFormValues = {
  values: [],
  shopifyReason: "UNKNOWN",
  images: "NOT_AVAILABLE",
  comments: "NOT_AVAILABLE",
  externalId: "",
  approvalRequired: false,
  skipReturnCost: false,
  skipReshipping: false,
  highlighted: false,
  reservedForLoggedUser: false,
};

export default function ReturnReasonForm({
  onSubmit,
  onRemove,
  defaultValues,
  error,
}: ReturnReasonFormProps) {
  const t = useTranslation();

  const form = useForm<ReturnReasonFormValues>({
    defaultValues: {
      ...DEFAULT_VALUES,
      ...defaultValues,
    },
  });

  const _onSubmit = form.handleSubmit(onSubmit);

  return (
    <Form {...form}>
      <form
        onSubmit={_onSubmit}
        className="flex flex-col justify-start gap-8 mt-[16px]"
      >
        {error && (
          <FormGlobalError
            title={error.title ?? t("misc.error")}
            error={error.error ?? t("errors.internalDescription")}
          />
        )}
        <Tabs defaultValue="translations">
          <ReturnReasonFormTabsList />
          <TabsContent value="translations">
            <ReturnReasonTranslationsForm />
          </TabsContent>
          <TabsContent value="options">
            <ReturnReasonOptionsForm />
          </TabsContent>
          <TabsContent value="return-rules">
            <ReturnReasonReturnRulesForm />
          </TabsContent>
        </Tabs>

        <div
          className={cn(
            "flex items-center gap-2",
            onRemove ? "justify-between" : "justify-center",
          )}
        >
          {onRemove && (
            <Button
              variant="destructive-outline"
              onClick={onRemove}
              type="button"
            >
              {t("misc.delete")}
            </Button>
          )}
          <FormSaveButton
            isDirty={form.formState.isDirty}
            isLoading={form.formState.isSubmitting}
          />
        </div>
      </form>
    </Form>
  );
}
